import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Loader2, 
  AlertCircle, 
  Flag, 
  BarChart2,
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { moderateText } from '../lib/moderation';
import { Flag as FlagType } from '../lib/types';
import { DocumentProcessor } from '../utils/documentProcessor';

const gradientAnimation = `
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

interface ModerationStep {
  id: string;
  name: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  icon: React.ReactNode;
  description: string;
}

// Change the ModerationResult interface definition to use a better approach
// Remove the index signature and use a proper type extension
interface ModerationResult {
  text: string;
  flags: FlagType[];
  overallToxicity: number;
  explanation?: string;
  summary: {
    spamScore: number;
    toxicityScore: number;
    profanityCount: number;
    emotionalIntensity: number;
    threatLevel: number;
    manipulationScore: number;
    credibilityScore: number;
    contextualRisk?: number;
  };
}

// First, add an interface for similar results returned from RAG
interface SimilarModeration {
  content: string;
  flags: FlagType[];
  overall_toxicity: number;
  similarity: number;
}

export function ModerateText() {
  const navigate = useNavigate();
  const visualizationRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string>("");
  const [showInput, setShowInput] = useState(true);
  const [showVisualization, setShowVisualization] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [moderationResult, setModerationResult] = useState<ModerationResult | null>(null);
  const [moderationSteps, setModerationSteps] = useState<ModerationStep[]>([
    {
      id: 'text-input',
      name: 'Text Input',
      status: 'pending',
      icon: <Activity className="w-5 h-5" />,
      description: 'Enter text to analyze'
    },
    {
      id: 'processing',
      name: 'Processing',
      status: 'pending',
      icon: <Loader2 className="w-5 h-5" />,
      description: 'Analyzing content'
    },
    {
      id: 'flag-detection',
      name: 'Flag Detection',
      status: 'pending',
      icon: <Flag className="w-5 h-5" />,
      description: 'Identifying content flags'
    },
    {
      id: 'toxicity-analysis',
      name: 'Toxicity Analysis',
      status: 'pending',
      icon: <AlertCircle className="w-5 h-5" />,
      description: 'Calculating toxicity levels'
    },
    {
      id: 'results',
      name: 'Results',
      status: 'pending',
      icon: <BarChart2 className="w-5 h-5" />,
      description: 'Generating final report'
    }
  ]);

  const [detectedFlags, setDetectedFlags] = useState<FlagType[]>([]);
  const [toxicityScore, setToxicityScore] = useState<number>(0);
  const [similarModerations, setSimilarModerations] = useState<SimilarModeration[]>([]);

  useEffect(() => {
    if (showVisualization && visualizationRef.current) {
      visualizationRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showVisualization]);

  const handleAnalyze = async () => {
    if (!text.trim()) return;
    
    setIsAnalyzing(true);
    setError("");
    
    // Start the transition
    setShowInput(false);
    
    // Wait for the input to fade out before showing visualization
    setTimeout(() => {
      setShowVisualization(true);
    }, 300);

    // Reset steps
    setModerationSteps(steps => 
      steps.map(step => ({ ...step, status: 'pending' }))
    );

    try {
      // Update processing step
      setModerationSteps(steps => 
        steps.map(step => 
          step.id === 'processing' 
            ? { ...step, status: 'processing' } 
            : step
        )
      );

      // Initialize RAG system
      console.log("Initializing RAG system...");
      try {
        await DocumentProcessor.initialize();
        console.log("RAG system initialized successfully");
      } catch (ragError) {
        console.error("Error initializing RAG system:", ragError);
        // Continue with moderation even if RAG fails
      }

      // Run the moderation
      console.log("Running moderation on text:", text);
      const result = await moderateText(text);
      if (!result) {
        throw new Error('No analysis results received');
      }
      console.log("Moderation result:", result);

      // Store the result for the transition
      setModerationResult(result as ModerationResult);

      // Check for similar past moderations
      console.log("Checking for similar past moderations...");
      try {
        const similarResults = await DocumentProcessor.searchSimilarModerations(text);
        console.log("Similar moderations found:", similarResults);
        
        // Store similar results for display
        setSimilarModerations(similarResults);

        // Check if content already exists with high similarity
        const contentAlreadyExists = similarResults.some((similar: SimilarModeration) => 
          similar.content === text || similar.similarity > 0.95
        );
        
        if (!contentAlreadyExists) {
          // Store the current moderation result in Supabase
          console.log("Storing moderation result in database...");
          await DocumentProcessor.storeModerationResult({
            content: text,
            flags: result.flags,
            overallToxicity: result.overallToxicity,
            metadata: {
              timestamp: new Date().toISOString(),
              type: 'web_moderation'
            }
          });
          console.log("Moderation result stored successfully");
        } else {
          console.log("Content already exists in database, skipping storage");
        }
      } catch (storageError) {
        console.error("Error storing moderation result:", storageError);
        // Continue with the process even if storage fails
      }

      // Simulate step-by-step processing with animations
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update flag detection step
      setModerationSteps(steps => 
        steps.map(step => 
          step.id === 'flag-detection' 
            ? { ...step, status: 'completed' } 
            : step
        )
      );
      setDetectedFlags(result.flags);

      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Update toxicity analysis step
      setModerationSteps(steps => 
        steps.map(step => 
          step.id === 'toxicity-analysis' 
            ? { ...step, status: 'completed' } 
            : step
        )
      );
      setToxicityScore(result.overallToxicity);

      await new Promise(resolve => setTimeout(resolve, 600));
      
      // Update results step
      setModerationSteps(steps => 
        steps.map(step => 
          step.id === 'results' 
            ? { ...step, status: 'completed' } 
            : step
        )
      );

      // After all steps are complete, wait a moment
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Start the transition to results
      setShowVisualization(false);
      
      // Wait for the fade-out animation to complete
      setTimeout(() => {
        setShowResults(true);
      }, 300);

    } catch (err) {
      console.error('Analysis error:', err);
      setError("Failed to analyze text. Please try again.");
      setModerationSteps(steps => 
        steps.map(step => 
          step.status === 'processing' 
            ? { ...step, status: 'error' } 
            : step
        )
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleClear = () => {
    setText("");
    setError("");
    setShowVisualization(false);
    setShowResults(false);
    setShowInput(true);
    setModerationResult(null);
    setModerationSteps(steps => 
      steps.map(step => ({ ...step, status: 'pending' }))
    );
    setDetectedFlags([]);
    setToxicityScore(0);
  };

  const getStepIcon = (status: ModerationStep['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
        return <Loader2 className="w-5 h-5 animate-spin" />;
      default:
        return <Activity className="w-5 h-5 text-neutral-400" />;
    }
  };

  const getFlagDescription = (flagType: string): string => {
    const descriptions: Record<string, string> = {
      profanity: "Content contains offensive language or swear words that may be inappropriate in many contexts.",
      hate_speech: "Content expresses discrimination, prejudice, or hostility toward individuals based on attributes like race, religion, or gender.",
      personal_attack: "Content contains direct insults or negative remarks targeting specific individuals.",
      sexual_content: "Content includes explicit references to sexual activities or sexually suggestive material.",
      harassment: "Content involves persistent or severe targeting of an individual or group with unwanted attention.",
      violence: "Content contains explicit descriptions or threats of physical harm or violence.",
      self_harm: "Content references or encourages self-injurious behaviors.",
      terrorism: "Content promotes terrorist acts or extremist ideologies.",
      misinformation: "Content contains false or misleading information presented as fact.",
      fraud: "Content attempts to deceive others for financial or personal gain.",
      neutral: "Content appears to be neutral or positive and does not contain harmful elements.",
      positive: "Content is positive and does not contain any harmful elements."
    };

    // Handle neutral or positive content specially
    if (flagType.toLowerCase() === 'neutral' || flagType.toLowerCase() === 'positive') {
      return descriptions[flagType.toLowerCase()];
    }

    return descriptions[flagType.toLowerCase()] || 
      "This content has been flagged for potentially violating community guidelines.";
  };

  const getIntentClassification = (toxicity: number, flags: FlagType[]): string => {
    if (toxicity > 0.8) {
      return "The content appears to be intentionally harmful or offensive.";
    } else if (toxicity > 0.5) {
      return "The content may be inappropriate but lacks clear harmful intent.";
    } else if (flags.length > 0) {
      return "The content contains some flagged elements but likely without harmful intent.";
    } else {
      return "The content appears to be appropriate with no harmful intent detected.";
    }
  };

  interface AudienceSuitability {
    name: string;
    suitable: boolean;
  }

  const getAudienceSuitability = (toxicity: number, flags: FlagType[]): AudienceSuitability[] => {
    const hasSevereFlag = flags.some(flag => flag.severity === 'high');
    
    return [
      { 
        name: "General Audience", 
        suitable: toxicity < 0.3 && !hasSevereFlag 
      },
      { 
        name: "Teens (13+)", 
        suitable: toxicity < 0.5 && !flags.some(f => 
          ['sexual_content', 'violence', 'hate_speech', 'self_harm'].includes(f.type.toLowerCase())
        ) 
      },
      { 
        name: "Adults Only", 
        suitable: true  // All content is suitable for adults, with appropriate warnings
      },
      { 
        name: "Professional Settings", 
        suitable: toxicity < 0.4 && !flags.some(f => f.severity === 'high')
      },
      { 
        name: "Educational Contexts", 
        suitable: toxicity < 0.6 || (toxicity < 0.8 && !flags.some(f => 
          ['hate_speech', 'sexual_content'].includes(f.type.toLowerCase())
        ))
      }
    ];
  };

  interface Recommendation {
    title: string;
    description: string;
    icon: React.ReactNode;
  }

  const getRecommendations = (toxicity: number, flags: FlagType[]): Recommendation[] => {
    const recommendations: Recommendation[] = [];
    
    if (toxicity > 0.7) {
      recommendations.push({
        title: "Consider Complete Revision",
        description: "The content has a high toxicity score. Consider rewriting it entirely to remove problematic elements.",
        icon: <AlertCircle className="w-5 h-5 text-red-400" />
      });
    } else if (toxicity > 0.4) {
      recommendations.push({
        title: "Tone Moderation Needed",
        description: "Consider revising the tone and specific phrases to reduce the overall toxicity.",
        icon: <AlertTriangle className="w-5 h-5 text-yellow-400" />
      });
    }
    
    // Add flag-specific recommendations
    flags.forEach(flag => {
      if (flag.severity === 'high') {
        recommendations.push({
          title: `Address ${flag.type} Content`,
          description: `Remove or significantly modify the ${flag.type.toLowerCase()} content to make it appropriate.`,
          icon: <Flag className="w-5 h-5 text-red-400" />
        });
      }
    });
    
    // If content is mostly fine
    if (toxicity < 0.3 && flags.length === 0) {
      recommendations.push({
        title: "Content Appears Appropriate",
        description: "No significant issues detected with this content.",
        icon: <CheckCircle2 className="w-5 h-5 text-green-500" />
      });
    }
    
    // Always add context recommendation
    recommendations.push({
      title: "Consider Context",
      description: "Remember that content appropriateness depends on the context, audience, and platform where it will be shared.",
      icon: <Activity className="w-5 h-5 text-blue-400" />
    });
    
    return recommendations;
  };

  return (
    <main className="min-h-screen bg-black text-white p-6">
      <style>{gradientAnimation}</style>
      <div className="max-w-4xl mx-auto pt-24">
        <AnimatePresence mode="wait">
          {showInput && (
            <motion.div
              key="input"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-12"
              >
                <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
                  Content Moderation Tool
                </h1>
                <p className="text-neutral-400 text-lg">
                  Enter your text below to check for any offensive or inappropriate content.
                </p>
              </motion.div>

              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => navigate("/")}
                className="mb-8 inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/10"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </motion.button>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="relative mb-8"
              >
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Type or paste your text here..."
                  className="w-full h-64 bg-white/5 border border-white/10 rounded-xl p-6 text-white placeholder:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none text-lg"
                  disabled={isAnalyzing}
                />
                <div className="absolute bottom-4 right-4 text-neutral-500 text-sm">
                  {text.length} characters
                </div>
              </motion.div>

              <div className="flex justify-center gap-4 mb-12">
                <button
                  onClick={handleAnalyze}
                  disabled={!text.trim() || isAnalyzing}
                  className={`
                    px-12 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2
                    bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500
                    hover:from-blue-600 hover:via-purple-600 hover:to-blue-600
                    text-white shadow-[0_0_20px_rgba(79,70,229,0.3)]
                    hover:shadow-[0_0_25px_rgba(79,70,229,0.45)]
                    animate-[gradient_8s_ease_infinite]
                    bg-[length:200%_200%]
                    ${(!text.trim() || isAnalyzing) && "opacity-50 cursor-not-allowed"}
                  `}
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Activity className="w-5 h-5" />
                      Analyze Text
                    </>
                  )}
                </button>

                <button
                  onClick={handleClear}
                  disabled={!text.trim()}
                  className={`
                    px-8 py-4 rounded-xl font-bold text-lg transition-colors
                    bg-neutral-800 hover:bg-neutral-700 text-white
                    ${!text.trim() && "opacity-50 cursor-not-allowed"}
                  `}
                >
                  Clear
                </button>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 mb-8"
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span>{error}</span>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {showVisualization && (
            <motion.div
              key="visualization"
              ref={visualizationRef}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white/5 rounded-xl p-8 space-y-8 backdrop-blur-sm"
            >
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => navigate("/")}
                className="mb-6 inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/10"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </motion.button>
              
              <motion.h2 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-2xl font-bold text-center mb-8"
              >
                Moderation Process
              </motion.h2>
              
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-5 gap-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {moderationSteps.map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1 }}
                    className={`
                      p-4 rounded-xl flex flex-col items-center text-center
                      ${step.status === 'completed' ? 'bg-green-500/10 border border-green-500/20' :
                        step.status === 'error' ? 'bg-red-500/10 border border-red-500/20' :
                        step.status === 'processing' ? 'bg-blue-500/10 border border-blue-500/20' :
                        'bg-white/5 border border-white/10'}
                    `}
                  >
                    <div className="mb-3">
                      {getStepIcon(step.status)}
                    </div>
                    <h3 className="font-medium mb-1">{step.name}</h3>
                    <p className="text-sm text-neutral-400">{step.description}</p>
                  </motion.div>
                ))}
              </motion.div>

              {toxicityScore > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium">Toxicity Score</span>
                    <span className="text-2xl font-bold">
                      {(toxicityScore * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${toxicityScore * 100}%` }}
                      exit={{ width: 0 }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className={`
                        h-full rounded-full
                        ${toxicityScore < 0.3 ? 'bg-green-500' :
                          toxicityScore < 0.7 ? 'bg-yellow-500' :
                          'bg-red-500'}
                      `}
                    />
                  </div>
                </motion.div>
              )}

              {detectedFlags.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-xl font-bold text-center">Detected Flags</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {detectedFlags.map((flag, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ delay: index * 0.1 }}
                        className={`
                          p-4 rounded-xl flex items-center gap-3
                          ${flag.severity === 'high' ? 'bg-red-500/10 border border-red-500/20' :
                            flag.severity === 'medium' ? 'bg-yellow-500/10 border border-yellow-500/20' :
                            'bg-green-500/10 border border-green-500/20'}
                        `}
                      >
                        <Flag className={`
                          w-5 h-5
                          ${flag.severity === 'high' ? 'text-red-400' :
                            flag.severity === 'medium' ? 'text-yellow-400' :
                            'text-green-400'}
                        `} />
                        <div>
                          <div className="font-medium">{flag.type}</div>
                          <div className="text-sm text-neutral-400">
                            {(flag.confidence * 100).toFixed(0)}% confidence
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {showResults && moderationResult && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white/5 rounded-xl p-8 space-y-8 backdrop-blur-sm"
            >
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => navigate("/")}
                className="mb-6 inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors bg-black/70 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/10"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </motion.button>
              
              <motion.h2 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-2xl font-bold text-center mb-8"
              >
                Detailed Analysis
              </motion.h2>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="space-y-6"
              >
                <div className="bg-white/5 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">Toxicity Analysis</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-lg">Overall Toxicity</span>
                      <span className="text-2xl font-bold">
                        {(moderationResult.overallToxicity * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${moderationResult.overallToxicity * 100}%` }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        className={`
                          h-full rounded-full
                          ${moderationResult.overallToxicity < 0.3 ? 'bg-green-500' :
                            moderationResult.overallToxicity < 0.7 ? 'bg-yellow-500' :
                            'bg-red-500'}
                        `}
                      />
                    </div>
                    <div className="p-4 rounded-lg bg-white/5 mt-4">
                      <h4 className="font-medium mb-2">What does this mean?</h4>
                      <p className="text-neutral-300 text-sm">
                        {moderationResult.overallToxicity < 0.3 ? 
                          'This content has a low toxicity score and is generally considered safe.' :
                          moderationResult.overallToxicity < 0.7 ?
                          'This content has a moderate toxicity score and may contain inappropriate elements.' :
                          'This content has a high toxicity score and contains problematic elements that should be addressed.'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4">Detected Flags</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {moderationResult.flags.map((flag, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 + index * 0.1 }}
                        className={`
                          p-4 rounded-xl flex flex-col bg-white/5
                          ${flag.severity === 'high' ? 'border-l-4 border-red-500' :
                            flag.severity === 'medium' ? 'border-l-4 border-yellow-500' :
                            'border-l-4 border-green-500'}
                        `}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <Flag className={`
                            w-5 h-5
                            ${flag.severity === 'high' ? 'text-red-400' :
                              flag.severity === 'medium' ? 'text-yellow-400' :
                              'text-green-400'}
                          `} />
                          <div className="flex-1">
                            <div className="font-medium text-lg">{flag.type}</div>
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-neutral-400">
                                {flag.severity === 'high' ? 'High severity' :
                                 flag.severity === 'medium' ? 'Medium severity' :
                                 'Low severity'} 
                              </span>
                              <span className="text-sm font-bold">
                                {(flag.confidence * 100).toFixed(0)}% confidence
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-neutral-300 mt-2">
                          {getFlagDescription(flag.type)}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white/5 rounded-xl p-6"
                >
                  <h3 className="text-xl font-bold mb-4">Context Analysis</h3>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-white/5">
                      <h4 className="font-medium mb-2">Intent Classification</h4>
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-300">
                          {getIntentClassification(moderationResult.overallToxicity, moderationResult.flags)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-4 rounded-lg bg-white/5">
                      <h4 className="font-medium mb-2">Audience Suitability</h4>
                      <div className="space-y-2">
                        {getAudienceSuitability(moderationResult.overallToxicity, moderationResult.flags).map((audience, i) => (
                          <div key={i} className="flex items-center gap-2">
                            {audience.suitable ? 
                              <CheckCircle2 className="w-4 h-4 text-green-500" /> : 
                              <XCircle className="w-4 h-4 text-red-500" />
                            }
                            <span className="text-neutral-300 text-sm">{audience.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>

                {moderationResult.explanation && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white/5 rounded-xl p-6"
                  >
                    <h3 className="text-xl font-bold mb-4">Analysis Details</h3>
                    <p className="text-neutral-300">{moderationResult.explanation}</p>
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="bg-white/5 rounded-xl p-6"
                >
                  <h3 className="text-xl font-bold mb-4">Recommendations</h3>
                  <div className="space-y-3">
                    {getRecommendations(moderationResult.overallToxicity, moderationResult.flags).map((recommendation, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                        <div className="flex-shrink-0 mt-1">
                          {recommendation.icon}
                        </div>
                        <div>
                          <h4 className="font-medium">{recommendation.title}</h4>
                          <p className="text-sm text-neutral-300">{recommendation.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="bg-white/5 rounded-xl p-6"
                >
                  <h3 className="text-xl font-bold mb-4">Similar Past Content</h3>
                  {similarModerations.length > 0 ? (
                    <div className="space-y-4">
                      <p className="text-neutral-400 text-sm mb-4">
                        Our system found {similarModerations.length} similar past moderation {similarModerations.length === 1 ? 'case' : 'cases'} in the database:
                      </p>
                      {similarModerations.slice(0, 3).map((similar, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 + index * 0.1 }}
                          className="p-4 rounded-lg bg-white/5 border border-white/10"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-sm font-medium text-neutral-300">Similar Content</span>
                            <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full">
                              {(similar.similarity * 100).toFixed(1)}% match
                            </span>
                          </div>
                          <p className="text-sm text-neutral-400 mb-3 line-clamp-2">{similar.content}</p>
                          <div className="flex flex-wrap gap-2">
                            {similar.flags.map((flag, i) => (
                              <span 
                                key={i}
                                className={`
                                  text-xs px-2 py-1 rounded-full
                                  ${flag.severity === 'high' ? 'bg-red-500/20 text-red-300' :
                                    flag.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                                    'bg-green-500/20 text-green-300'}
                                `}
                              >
                                {flag.type}
                              </span>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                      {similarModerations.length > 3 && (
                        <p className="text-center text-neutral-400 text-sm">
                          + {similarModerations.length - 3} more similar cases
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
                      <p className="text-neutral-400">No similar content found in our database.</p>
                      <p className="text-neutral-500 text-sm mt-2">This appears to be a unique case that will help train our system.</p>
                    </div>
                  )}
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="bg-white/5 rounded-xl p-6"
                >
                  <h3 className="text-xl font-bold mb-4">Moderation Intelligence</h3>
                  <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-white/5">
                      <h4 className="font-medium mb-2">Content Pattern Analysis</h4>
                      <p className="text-neutral-300 text-sm">
                        {moderationResult.flags.length > 1 ? 
                          `This content contains multiple flags (${moderationResult.flags.map(f => f.type).join(", ")}), a pattern seen in ${
                            similarModerations.filter(m => m.flags.length > 1).length
                          } other cases in our database.` : 
                          moderationResult.flags.length === 1 ?
                          `This content contains a single flag type (${moderationResult.flags[0].type}), which appears in ${
                            similarModerations.filter(m => m.flags.some(f => f.type === moderationResult.flags[0].type)).length
                          } other cases in our database.` :
                          "This content doesn't contain any flags, which is consistent with appropriate content patterns."
                        }
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-white/5">
                      <h4 className="font-medium mb-2">Learning Impact</h4>
                      <p className="text-neutral-300 text-sm">
                        {similarModerations.length === 0 ? 
                          "This moderation is unique in our database and will significantly improve our system's knowledge." :
                          similarModerations.length < 3 ?
                          "This moderation adds to a small set of similar cases, strengthening our system's understanding of this content type." :
                          "Our system has encountered several similar cases, allowing for high-confidence moderation decisions."
                        }
                      </p>
                      <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, similarModerations.length * 10)}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                          className="h-full rounded-full bg-indigo-500"
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-neutral-500">Unique Case</span>
                        <span className="text-xs text-neutral-500">Well Understood</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="bg-white/5 rounded-xl p-6"
                >
                  <h3 className="text-xl font-bold mb-4">Contextual Intelligence</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-white/5">
                      <h4 className="font-medium mb-2">Semantic Understanding</h4>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                          <BarChart2 className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="font-medium">
                            {moderationResult.flags.some(f => f.type === 'profanity') ? 'Explicit Language' : 
                             moderationResult.flags.some(f => f.type === 'hate_speech') ? 'Harmful Speech' :
                             moderationResult.flags.some(f => f.type === 'personal_attack') ? 'Targeted Negativity' :
                             'Normal Communication'}
                          </div>
                          <div className="text-sm text-neutral-400">
                            {moderationResult.flags.length > 0 ? 
                              `Based on ${similarModerations.length} similar cases` : 
                              'No problematic patterns detected'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-white/5">
                      <h4 className="font-medium mb-2">Consistency Check</h4>
                      <div className="flex items-center gap-3">
                        <div className={`
                          w-12 h-12 rounded-full flex items-center justify-center
                          ${similarModerations.length > 0 ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-gradient-to-br from-yellow-500 to-amber-600'}
                        `}>
                          {similarModerations.length > 0 ? 
                            <CheckCircle2 className="w-6 h-6" /> : 
                            <AlertCircle className="w-6 h-6" />}
                        </div>
                        <div>
                          <div className="font-medium">
                            {similarModerations.length > 0 ? 'Consistent Moderation' : 'New Pattern Detected'}
                          </div>
                          <div className="text-sm text-neutral-400">
                            {similarModerations.length > 0 ? 
                              `This moderation aligns with past decisions` : 
                              'This is a new pattern for our system'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}